/**
 * Boot instrumentation and the reload-once guard.
 *
 * LESSON PINNED AT THE SCENE (Journal post-mortem §6, §8):
 *  1. "The app sometimes takes ages to load" is unactionable. Stamped phases with a
 *     per-boot counter turn it into "data-ready is 4s on boot #3 after a resume".
 *  2. A guard that prevents an infinite reload loop must itself survive the reload
 *     it triggers. A module-level `let hasReloaded = false` is reset by the very
 *     reload it is guarding — that is a reload loop with extra steps. The guard
 *     lives in sessionStorage: it survives a reload, and dies with the tab.
 */
import { safeGet, safeRemove, safeSet } from '../platform/storage.js';
import { instantISO } from '../time/date.js';
const DEFAULT_KEY = 'mononoke.boot-history.v1';
const DEFAULT_HISTORY = 20;
export class BootWaterfall {
    storage;
    key;
    history;
    now;
    clock;
    startMs;
    record;
    constructor(kind, options = {}) {
        this.storage = options.storage;
        this.key = options.key ?? DEFAULT_KEY;
        this.history = options.history ?? DEFAULT_HISTORY;
        this.now = options.now ?? (() => Date.now());
        this.clock = options.clock ?? (() => new Date());
        this.startMs = this.now();
        const past = this.loadHistory();
        this.record = {
            bootNumber: (past[past.length - 1]?.bootNumber ?? 0) + 1,
            kind,
            startedAt: instantISO(this.clock()),
            stamps: [],
        };
    }
    loadHistory() {
        if (!this.storage)
            return [];
        const raw = safeGet(this.storage, this.key);
        if (raw === null)
            return [];
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        }
        catch {
            return [];
        }
    }
    stamp(phase) {
        const stamp = { phase, atMs: this.now() - this.startMs };
        this.record.stamps.push(stamp);
        if (this.storage) {
            const all = [...this.loadHistory().filter((r) => r.bootNumber !== this.record.bootNumber), this.record];
            safeSet(this.storage, this.key, JSON.stringify(all.slice(-this.history)));
        }
        return stamp;
    }
    current() {
        return this.record;
    }
    past() {
        return this.loadHistory();
    }
    /** One line per boot, for the Diagnostics screen and support pastes. */
    toSupportText() {
        // past() re-reads storage, which stamp() has already written this boot into —
        // appending this.record again printed the current boot twice. Visible in the
        // first real support report (25 Jul 2026). A diagnostic that miscounts its own
        // events is worse than no diagnostic.
        const history = this.past().filter((r) => r.bootNumber !== this.record.bootNumber);
        return [...history, this.record]
            .slice(-10)
            .map((r) => {
            const phases = r.stamps.map((s) => `${s.phase}=${String(Math.round(s.atMs))}ms`).join(' ');
            return `#${String(r.bootNumber)} ${r.kind} ${r.startedAt} ${phases}`;
        })
            .join('\n');
    }
}
/**
 * A guard for actions that trigger a reload (applying a new service worker,
 * recovering from a corrupt cache). Backed by sessionStorage in the app: it
 * survives the reload it causes, and is gone when the tab closes.
 */
export class ReloadGuard {
    storage;
    key;
    constructor(storage, key = 'mononoke.reload-guard.v1') {
        this.storage = storage;
        this.key = key;
    }
    /** True if a reload has already been performed for this reason in this tab. */
    hasReloaded(reason) {
        return safeGet(this.storage, `${this.key}:${reason}`) !== null;
    }
    /**
     * Mark before reloading, never after — code after `location.reload()` may not run.
     * Returns false when a reload for this reason already happened, which is the
     * signal to surface an error instead of looping.
     */
    markAndAllow(reason) {
        if (this.hasReloaded(reason))
            return false;
        const outcome = safeSet(this.storage, `${this.key}:${reason}`, '1');
        // If the mark cannot be persisted, refuse the reload. An unguarded reload is
        // an infinite loop; a refused one is a visible, reportable failure.
        return outcome.ok;
    }
    clear(reason) {
        safeRemove(this.storage, `${this.key}:${reason}`);
    }
}
//# sourceMappingURL=waterfall.js.map